import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import { PromoCode } from 'common/contracts';
import { Coupon, Offer } from 'common/contracts';
import dayjs from 'dayjs';
import { EStatusLabel } from 'design-system/StatusLabel/types';
import { EToasterStatus, showToast } from 'utils/showToast';
import * as yup from 'yup';

import {
  CouponFormValues,
  ExpirationMode,
  UpsertCouponDto,
  UpsertCouponWithPromoCodeDto,
  UpsertPromoCodeDto
} from './types';

export const STATUS_TO_ICON_MAP = {
  [EStatusLabel.SUCCESS]: <CheckCircleOutlineOutlinedIcon />,
  [EStatusLabel.FAILED]: <DoNotDisturbAltOutlinedIcon />,
  [EStatusLabel.PENDING]: <AccessTimeOutlinedIcon />
};

export function isDatePassed(timestamp: string) {
  return dayjs().isAfter(dayjs(timestamp));
}

export const generatePromoCodesDto = (
  promoCodes: PromoCode[],
  couponId?: string
): UpsertPromoCodeDto[] => {
  return promoCodes.map(({ name, maxRedemptions, active, id, fresh }) => {
    const data: UpsertPromoCodeDto = {
      name,
      maxRedemptions,
      active,
      couponId
    };

    if (id && !fresh) {
      data.id = id;
    }

    return data;
  });
};

export const generateCouponDto = (
  values: CouponFormValues,
  neverExpires: boolean
): UpsertCouponDto => {
  return {
    name: values.name,
    active: values.active,
    discountPercentage: Number(values.discountPercentage),
    maxRedemptionsPerCustomer: values.maxRedemptionsPerCustomer
      ? Number(values.maxRedemptionsPerCustomer)
      : null,
    startsAt:
      values.expirationMode === 'no-expiration'
        ? null
        : values.startsAt || null,
    expiredBy:
      values.expirationMode === 'start-and-end' ? values.expiredBy : null,
    firstTimePurchase: values.firstTimePurchase,
    supportedOffersBySku: values.supportedOffersBySku,
    allowedPlayers: values.allowedPlayers
  };
};

export const checkUniqueness = (
  newName: string = '',
  coupons: Coupon[] | undefined,
  currentCouponName: string = '',
  isEdit: boolean
): boolean => {
  const couponsNames = coupons?.map((coupon) => coupon.name);
  if (isEdit) {
    return !couponsNames
      ?.filter((name) => name !== currentCouponName)
      .includes(newName);
  }
  return !couponsNames?.includes(newName);
};

export const getOffersForSelect = (
  offersMap: { bundles?: Offer[]; promotions?: Offer[]; popups?: Offer[] },
  supportedOffersByType: string[] = ['bundles', 'promotions', 'popups']
) => {
  const offers = [];
  if (offersMap.bundles && supportedOffersByType.includes('bundles')) {
    offers.push(
      ...offersMap.bundles.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  if (offersMap.promotions && supportedOffersByType.includes('promotions')) {
    offers.push(
      ...offersMap.promotions.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  if (offersMap.popups && supportedOffersByType.includes('popups')) {
    offers.push(
      ...offersMap.popups.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  return offers;
};

export const handleUpsertSuccess = async ({
  getSingleCoupon,
  helpers,
  isEdit
}: {
  getSingleCoupon: any;
  helpers: any;
  isEdit: boolean;
}) => {
  await getSingleCoupon.refetch();
  if (getSingleCoupon.data) {
    helpers.setValues({
      ...getSingleCoupon.data,
      promoCodes: (getSingleCoupon.data.promoCodes || []).map(
        (promoCode: any) => ({
          ...promoCode,
          fresh: false
        })
      )
    });
  }
  showToast({
    message: `Coupon ${isEdit ? 'updated' : 'created'} successfully`,
    status: EToasterStatus.SUCCESS
  });
};

export const handleUpsertError = (error: any, isEdit: boolean) => {
  const serverError = error?.response?.data?.message;
  showToast({
    message: serverError || `Error ${isEdit ? 'updating' : 'creating'} coupon`,
    status: EToasterStatus.ERROR
  });
};

export const deriveExpirationMode = (
  startsAt?: string | null,
  expiredBy?: string | null
): ExpirationMode => {
  return expiredBy
    ? 'start-and-end'
    : startsAt
      ? 'start-only'
      : 'no-expiration';
};

export const applyExpirationModeChange = (
  mode: ExpirationMode,
  setFieldValue: (field: string, value: any) => void,
  setFieldTouched: (field: string, isTouched?: boolean | undefined) => void,
  setNeverExpires: (value: boolean) => void
) => {
  if (mode === 'no-expiration') {
    setNeverExpires(true);
    setFieldValue('startsAt', null);
    setFieldValue('expiredBy', null);
    setFieldTouched('startsAt', false);
    setFieldTouched('expiredBy', false);
    return;
  }
  setNeverExpires(false);
  if (mode === 'start-only') {
    setFieldValue('expiredBy', null);
    setFieldTouched('expiredBy', false);
  }
};

export const buildCouponValidationSchema = (params: {
  checkUniqueness: (
    newName: string,
    coupons: Coupon[] | undefined,
    currentCouponName: string,
    isEdit: boolean
  ) => boolean;
  getCouponsData: Coupon[] | undefined;
  currentCouponName: string;
  isEdit: boolean;
  isCheckoutUser: boolean;
}) => {
  const {
    checkUniqueness,
    getCouponsData,
    currentCouponName,
    isEdit,
    isCheckoutUser
  } = params;

  return yup.object().shape({
    name: yup
      .string()
      .required('Coupon name is required')
      .test('uniqueness', 'Coupon name already exists', (value) => {
        return checkUniqueness(
          value || '',
          getCouponsData,
          currentCouponName || '',
          isEdit
        );
      }),
    active: yup.boolean().required('Coupon status is required'),
    discountPercentage: yup
      .number()
      .integer('Discount percentage must be a whole number')
      .min(1, 'Discount percentage must be at least 1')
      .max(99, 'Discount percentage must be at most 99')
      .required('Discount percentage is required'),
    maxRedemptionsPerCustomer: yup
      .number()
      .min(0, 'Max redemptions per customer must be greater than or equal to 1')
      .notRequired(),
    startsAt: yup
      .date()
      .nullable()
      .when('expirationMode', {
        is: (mode: string) => mode === 'start-only' || mode === 'start-and-end',
        then: (schema) =>
          schema
            .min(dayjs().toDate(), 'Start date should be in the future')
            .required('Start date is required')
      }),
    expiredBy: yup
      .date()
      .nullable()
      .min(dayjs().toDate(), 'Expires on date should be in the future')
      .when('startsAt', (startValue: unknown, schema: any) => {
        if (!startValue) return schema;
        const parsedStart = dayjs(startValue as unknown as string);
        return parsedStart.isValid()
          ? schema.min(
              parsedStart.toDate(),
              'Expiry date must be after start date'
            )
          : schema;
      })
      .when('expirationMode', {
        is: (mode: string) => mode === 'start-and-end',
        then: (schema) => schema.required('Expiry date is required')
      }),
    firstTimePurchase: yup.boolean(),
    ...(isCheckoutUser
      ? {}
      : {
          supportedOffersBySku: yup.array().of(yup.string())
        }),
    promoCodes: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Promo code name is required'),
          maxRedemptions: yup
            .number()
            .min(0, 'Max redemptions must be greater than or equal to 0')
            .required('Max redemptions is required')
        })
      )
      .min(1, 'At least one promo code must be added')
  });
};
